import type { ClassicSessionProgress } from '../../types'
import { useLocale } from '../../i18n/context'

interface Props {
  progress: ClassicSessionProgress
  onStudyAgain: () => void
  onStudyUnknown: () => void
  onBack: () => void
  hasUnknown: boolean
  // Full deck card count — always the whole deck regardless of how many cards
  // the just-finished round contained (e.g. after a subset / unknown-only round).
  // Used only for the "Study Again (all N)" label; progress.total governs stats.
  deckCardCount: number
}

export default function StudyComplete({ progress, onStudyAgain, onStudyUnknown, onBack, hasUnknown, deckCardCount }: Props) {
  const { tr } = useLocale()
  const cs = tr.classicStudy
  const { total, known, unknown } = progress
  const pct = total > 0 ? Math.round((known / total) * 100) : 0

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg-page)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        gap: 32,
      }}
    >
      {/* Icon */}
      <div style={{ fontSize: 56, lineHeight: 1 }}>
        {pct === 100 ? '🌟' : pct >= 70 ? '🎉' : '💪'}
      </div>

      {/* Headline */}
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          {pct === 100 ? cs.perfectRound : cs.roundComplete}
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginTop: 8 }}>
          {cs.reviewed(total)}
        </p>
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '20px 32px',
        }}
      >
        <Stat value={known} label={cs.knownLabel} color="var(--success)" />
        <div style={{ width: 1, background: 'var(--border)' }} />
        <Stat value={unknown} label={cs.unknownLabel} color="var(--danger)" />
        <div style={{ width: 1, background: 'var(--border)' }} />
        <Stat value={`${pct}%`} label={cs.score} color="var(--accent)" />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 320 }}>
        {hasUnknown && (
          <button
            onClick={onStudyUnknown}
            style={{
              padding: '14px 24px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {cs.studyUnknown(unknown)}
          </button>
        )}

        <button
          onClick={onStudyAgain}
          style={{
            padding: '14px 24px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-primary)',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {cs.studyAgain(deckCardCount)}
        </button>

        <button
          onClick={onBack}
          style={{
            padding: '14px 24px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          {cs.backToDeck}
        </button>
      </div>
    </div>
  )
}

function Stat({ value, label, color }: { value: string | number; label: string; color: string }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 64 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{label}</div>
    </div>
  )
}
